import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, FileSpreadsheet, Download, Pencil, Package2, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { openBrandedPrintWindow } from "@/lib/documentTemplate";
import MaterialPickerDialog from "@/components/MaterialPickerDialog";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const empty = { title: "", project_id: "", gst_percent: 18, margin_percent: 15, notes: "", items: [{ description: "", unit: "Nos", quantity: 1, rate: 0, category: "Material", item_code: "" }] };

export default function BOQ() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [pickerFor, setPickerFor] = useState(null); // row index awaiting a material pick
  const [addingRows, setAddingRows] = useState(false); // Bulk-add picker

  const load = () => api.get("/boq").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/projects").then((r) => setProjects(r.data)); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (b) => {
    setForm({
      title: b.title || "", project_id: b.project_id || "",
      gst_percent: Number(b.gst_percent ?? 18), margin_percent: Number(b.margin_percent ?? 15),
      notes: b.notes || "",
      items: (b.items || []).map(i => ({ description: i.description||"", unit: i.unit||"Nos", quantity: Number(i.quantity||0), rate: Number(i.rate||0), category: i.category||"Material", item_code: i.item_code||"" })),
    });
    setEditId(b.id); setOpen(true);
  };

  const applyMaterial = (mat, atIndex) => {
    // Fill row at index with material info + price from library.
    const row = {
      description: `${mat.name}${mat.spec ? ` — ${mat.spec}` : ""}`,
      unit: mat.uom || "Nos",
      quantity: 1,
      rate: Number(mat.per_unit_price || 0),
      category: mat.category || "Material",
      item_code: mat.item_code,
    };
    if (atIndex === "append") {
      setForm(f => ({ ...f, items: [...f.items, row] }));
    } else {
      setForm(f => { const items = [...f.items]; items[atIndex] = row; return { ...f, items }; });
    }
    if (!row.rate) toast.warning(`${mat.name} has no price set in Material Library yet — enter it manually or set it in Materials.`);
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { description: "", unit: "Nos", quantity: 1, rate: 0, category: "Material", item_code: "" }] });
  const updItem = (i, patch) => { const items = [...form.items]; items[i] = { ...items[i], ...patch }; setForm({ ...form, items }); };
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    try {
      if (editId) { await api.patch(`/boq/${editId}`, form); toast.success("BOQ updated"); }
      else { await api.post("/boq", form); toast.success("BOQ created"); }
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch { toast.error("Failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this BOQ?")) return; await api.delete(`/boq/${id}`); load(); toast.success("Deleted"); };

  const convertToPO = async (b) => {
    if (!(b.items || []).length) return toast.error("No items to convert");
    if (!window.confirm(`Convert BOQ ${b.number || b.title} to Purchase Orders?\n\nItems will be grouped by their preferred supplier. Items without a supplier land in an "Unassigned" PO you can assign later.`)) return;
    try {
      const { data } = await api.post(`/boq/${b.id}/convert-to-po`);
      const msg = `${data.created} PO${data.created === 1 ? "" : "s"} created` + (data.unassigned_count ? ` · ${data.unassigned_count} item(s) unassigned` : "");
      toast.success(msg);
      nav("/purchase-orders");
    } catch (e) { toast.error(e.response?.data?.detail || "Conversion failed"); }
  };

  const subtotal = form.items.reduce((s, i) => s + (i.quantity || 0) * (i.rate || 0), 0);
  const margin = subtotal * (form.margin_percent / 100);
  const tax = (subtotal + margin) * (form.gst_percent / 100);
  const total = subtotal + margin + tax;

  const printBOQ = (b) => {
    const rupee = (n) => `₹${Number(n||0).toLocaleString("en-IN")}`;
    const body = `
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px">
        <div><h1 style="margin:0">${b.title || "Bill of Quantity"}</h1>
          <div style="color:#73706A;font-size:11px;margin-top:4px">Prepared for internal estimation & client quotation</div></div>
        <div style="text-align:right;font-size:11px;color:#73706A">
          <div style="font-size:9px;letter-spacing:1.5px;text-transform:uppercase">BOQ Ref</div>
          <div style="font-family:monospace">${b.number || "—"}</div>
        </div>
      </div>
      <table><thead><tr><th style="width:32px">#</th><th>Description</th><th style="width:60px">Unit</th><th class="right" style="width:60px">Qty</th><th class="right" style="width:90px">Rate</th><th class="right" style="width:110px">Amount</th></tr></thead><tbody>
      ${(b.items||[]).map((i,idx)=>`<tr><td>${idx+1}</td><td>${i.description||""}</td><td>${i.unit||""}</td><td class="right">${i.quantity}</td><td class="right">${rupee(i.rate)}</td><td class="right">${rupee((i.quantity||0)*(i.rate||0))}</td></tr>`).join("")}
      </tbody></table>
      <div style="margin-top:14px;margin-left:auto;width:300px;font-size:12px">
        <div style="display:flex;justify-content:space-between;padding:5px 0"><span>Subtotal</span><span>${rupee(b.subtotal)}</span></div>
        <div style="display:flex;justify-content:space-between;padding:5px 0;color:#73706A"><span>Margin (${b.margin_percent||0}%)</span><span>${rupee(b.margin_amount)}</span></div>
        <div style="display:flex;justify-content:space-between;padding:5px 0;color:#73706A"><span>GST (${b.gst_percent||0}%)</span><span>${rupee(b.tax_amount)}</span></div>
        <div style="display:flex;justify-content:space-between;padding:10px 0;margin-top:6px;border-top:2px solid #1A1918;font-weight:700;font-size:14px"><span>TOTAL</span><span>${rupee(b.total)}</span></div>
      </div>
      ${b.notes ? `<div style="margin-top:24px;padding:12px;background:#F7F5F0;border-radius:6px;font-size:11px;color:#3A3936"><b style="text-transform:uppercase;font-size:9px;letter-spacing:1.5px;color:#73706A">Notes / Terms</b><div style="margin-top:6px">${b.notes.replace(/\n/g,"<br>")}</div></div>` : ""}
    `;
    openBrandedPrintWindow(body, {
      subject: `BOQ ${b.number || ""} — ${b.title || ""}`.trim(),
      title: "Bill of Quantity",
      number: b.number || "",
      date: new Date(b.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" }),
    });
  };

  return (
    <div className="space-y-6" data-testid="boq-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Estimation</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">Bills of Quantity</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-boq-btn" onClick={openNew} className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-1.5" />New BOQ</Button></DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editId ? "Edit BOQ / Quotation" : "Create BOQ / Quotation"}</DialogTitle><DialogDescription>Line items, margin, GST and totals are computed live.</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger><SelectValue placeholder="Project (optional)" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex gap-2">
                <Input type="number" placeholder="Margin %" value={form.margin_percent} onChange={(e) => setForm({ ...form, margin_percent: Number(e.target.value) })} />
                <Input type="number" placeholder="GST %" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: Number(e.target.value) })} />
              </div>
            </div>
            <div className="mt-3">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  <tr className="border-b border-border/50"><th className="text-left py-2 w-20">Code</th><th className="text-left">Description</th><th>Unit</th><th>Qty</th><th>Rate</th><th className="text-right">Amount</th><th></th></tr>
                </thead>
                <tbody>
                  {form.items.map((it, i) => (
                    <tr key={i} className="border-b border-border/50">
                      <td className="py-1.5">
                        <button onClick={() => setPickerFor(i)} data-testid={`boq-pick-${i}`} className="flex items-center gap-1 text-[11px] font-mono text-primary hover:underline">
                          {it.item_code || <span className="text-muted-foreground italic">pick</span>}
                          <Package2 className="w-3 h-3 opacity-60" />
                        </button>
                      </td>
                      <td className="py-1.5"><Input value={it.description} onChange={(e) => updItem(i, { description: e.target.value })} /></td>
                      <td><Input className="w-20" value={it.unit} onChange={(e) => updItem(i, { unit: e.target.value })} /></td>
                      <td><Input className="w-20" type="number" value={it.quantity} onChange={(e) => updItem(i, { quantity: Number(e.target.value) })} /></td>
                      <td><Input className="w-24" type="number" value={it.rate} onChange={(e) => updItem(i, { rate: Number(e.target.value) })} /></td>
                      <td className="text-right pr-2">{fmt(it.quantity * it.rate)}</td>
                      <td><button onClick={() => rmItem(i)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-2 flex gap-2">
                <Button variant="ghost" onClick={addItem} className="text-primary" data-testid="boq-add-row"><Plus className="w-3.5 h-3.5 mr-1" /> Blank row</Button>
                <Button variant="ghost" onClick={() => setAddingRows(true)} className="text-primary" data-testid="boq-add-from-library"><Package2 className="w-3.5 h-3.5 mr-1" /> Add from Material Library</Button>
              </div>
            </div>
            <div className="ml-auto w-72 text-sm space-y-1">
              <div className="flex justify-between"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Margin ({form.margin_percent}%)</span><span>{fmt(margin)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>GST ({form.gst_percent}%)</span><span>{fmt(tax)}</span></div>
              <div className="flex justify-between font-display text-lg pt-2 border-t border-border/50"><span>Total</span><span>{fmt(total)}</span></div>
            </div>
            <Textarea placeholder="Notes / T&C" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <Button onClick={save} data-testid="save-boq-btn" className="bg-primary text-primary-foreground">{editId ? "Update BOQ" : "Save BOQ"}</Button>
          </DialogContent>
        </Dialog>

        {/* Material Picker — for row-level replace */}
        <MaterialPickerDialog
          open={pickerFor !== null}
          onClose={() => setPickerFor(null)}
          onPick={(m) => { applyMaterial(m, pickerFor); setPickerFor(null); }}
        />
        {/* Material Picker — bulk-append multiple items */}
        <MaterialPickerDialog
          open={addingRows}
          multi
          onClose={() => setAddingRows(false)}
          onPickMany={(mats) => { mats.forEach(m => applyMaterial(m, "append")); setAddingRows(false); toast.success(`${mats.length} item${mats.length>1?"s":""} added from library`); }}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map((b) => (
          <div key={b.id} data-testid={`boq-${b.id}`} className="rounded-2xl p-6 bg-card border border-border/60 hover:border-primary/40 transition-colors">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{b.number}</div>
                <div className="font-display text-xl mt-1">{b.title}</div>
              </div>
              <FileSpreadsheet className="w-5 h-5 text-primary" strokeWidth={1.6} />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
              <div><div className="text-muted-foreground uppercase tracking-[0.15em] text-[10px]">Items</div><div className="mt-1">{b.items?.length}</div></div>
              <div><div className="text-muted-foreground uppercase tracking-[0.15em] text-[10px]">GST</div><div className="mt-1">{b.gst_percent}%</div></div>
              <div><div className="text-muted-foreground uppercase tracking-[0.15em] text-[10px]">Total</div><div className="mt-1 font-semibold text-primary">{fmt(b.total)}</div></div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => printBOQ(b)} data-testid={`export-boq-${b.id}`}><Download className="w-3.5 h-3.5 mr-1.5" /> Export PDF</Button>
              <Button variant="outline" size="sm" onClick={() => openEdit(b)} data-testid={`edit-boq-${b.id}`}><Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit</Button>
              <Button size="sm" onClick={() => convertToPO(b)} data-testid={`convert-po-${b.id}`} className="bg-primary text-primary-foreground"><ShoppingCart className="w-3.5 h-3.5 mr-1.5" /> Convert to PO</Button>
              <Button variant="ghost" size="sm" onClick={() => remove(b.id)} data-testid={`delete-boq-${b.id}`} className="text-destructive ml-auto"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
